import React, { useState } from 'react';
import {
  Button,
  DialogContent,
  Typography,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { FormWithRedirect, TextInput, useNotify, useDataProvider, useTranslate } from 'react-admin';
import TerminalDatesInput from 'components/inputs/TerminalDatesInput';
import { DateTimeInput } from 'components/inputs';
import { useNotifyError } from 'utils/notifiers/useNotifyError';
import { updatePartialByIds } from '../services/partialUpdate';
import { ActionDialog } from './ActionDialog';
import { DialogConfirmActions } from './DialogConfirmActions';
import { SelectedEntitiesList } from './SelectedEntitiesList';
import { useBulkActionStyles } from './styles';

interface BuildingEntityBlockUnBlockProps {
  selectedEntities: any[];
  terminalId: string | number;
  entityType: 'place' | 'port';
  resource: string;
  idField: string;
  displayField: string;
  layout?: 'inline' | 'stacked';
}

export const BuildingEntityBlockUnBlock: React.FC<BuildingEntityBlockUnBlockProps> = ({ 
  selectedEntities, 
  terminalId,
  entityType,
  resource,
  idField,
  displayField,
  layout = 'inline'
}) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'block' | 'unblock' | null>(null);
  const [loading, setLoading] = useState(false);
  const classes = useBulkActionStyles();
  const notify = useNotify();
  const notifyError = useNotifyError();
  const dataProvider = useDataProvider();
  const translate = useTranslate();

  const record = { terminal_id: terminalId };

  // Separate entities by blocked status
  const blockedEntities = selectedEntities.filter(entity => entity.blocked);
  const enabledEntities = selectedEntities.filter(entity => !entity.blocked);

  const handleOpen = (actionMode: 'block' | 'unblock') => {
    setMode(actionMode);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    // Delay resetting mode until dialog animation completes
    setTimeout(() => setMode(null), 300);
  };

  const entitiesToProcess = mode === 'unblock' ? blockedEntities : enabledEntities;
  const isUnblockMode = mode === 'unblock';
  const entitySingular = translate(`osfc.storage_ui.entity.${entityType}`);
  const entityPlural = translate(`osfc.storage_ui.entity.${entityType}s`);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const updateData = isUnblockMode 
        ? {
            lock_datetime_from: null,
            lock_datetime_to: null,
            comment: null,
          }
        : {
            lock_datetime_from: values.lock_datetime_from,
            lock_datetime_to: values.lock_datetime_to,
            comment: values.comment,
          };

      const response = await updatePartialByIds(
        dataProvider,
        resource,
        entitiesToProcess.map(entity => entity[idField]),
        updateData
      );

      if (entitiesToProcess.length === 1) {
        // Update entity from response
        if (response?.data) {
          entitiesToProcess[0].blocked = response.data.blocked ?? !isUnblockMode;
          entitiesToProcess[0].lock_datetime_from = response.data.lock_datetime_from;
          entitiesToProcess[0].lock_datetime_to = response.data.lock_datetime_to;
          entitiesToProcess[0].comment = response.data.comment;
        } else {
          entitiesToProcess[0].blocked = !isUnblockMode;
        }
        
        notify(translate('osfc.storage_ui.block.success_single', { entity: entitySingular, action: translate(isUnblockMode ? 'osfc.storage_ui.block.unblocked' : 'osfc.storage_ui.block.blocked') }), { type: 'success' });
      } else {
        // Update entities from response
        if (response?.data && Array.isArray(response.data)) {
          response.data.forEach(updatedEntity => {
            const entity = entitiesToProcess.find(e => 
              e[idField] === updatedEntity[idField]
            );
            if (entity) {
              entity.blocked = updatedEntity.blocked ?? !isUnblockMode;
              entity.lock_datetime_from = updatedEntity.lock_datetime_from;
              entity.lock_datetime_to = updatedEntity.lock_datetime_to;
              entity.comment = updatedEntity.comment;
            }
          });
        } else {
          // Fallback if no response data
          entitiesToProcess.forEach(entity => {
            entity.blocked = !isUnblockMode;
          });
        }
        
        notify(
          translate('osfc.storage_ui.block.success_multi', { count: entitiesToProcess.length, entities: entityPlural, action: translate(isUnblockMode ? 'osfc.storage_ui.block.unblocked' : 'osfc.storage_ui.block.blocked') }),
          { type: 'success' }
        );
      }

      handleClose();
    } catch (error) {
      notifyError(error);
    } finally {
      setLoading(false);
    }
  };

  const initialValues = {
    lock_datetime_from: null,
    lock_datetime_to: null,
    comment: null,
  };

  const getDialogTitle = () => {
    if (entitiesToProcess.length === 1) {
      return translate(isUnblockMode ? 'osfc.storage_ui.block.unblock_title_single' : 'osfc.storage_ui.block.block_title_single', { entity: entitySingular, name: entitiesToProcess[0]?.[displayField] });
    }
    return translate(isUnblockMode ? 'osfc.storage_ui.block.unblock_title_multi' : 'osfc.storage_ui.block.block_title_multi', { count: entitiesToProcess.length, entities: entityPlural });
  };

  return (
    <>
      <div className={layout === 'stacked' ? classes.stackedButtonContainer : classes.inlineButtonContainer}>
        {enabledEntities.length > 0 && (
          <Button 
            onClick={() => handleOpen('block')} 
            size="small" 
            color="primary" 
            variant="outlined"
            className={classes.button}
            startIcon={<LockIcon />}
          >
            {translate('osfc.storage_ui.block.block_button', { count: enabledEntities.length })}
          </Button>
        )}

        {blockedEntities.length > 0 && (
          <Button 
            onClick={() => handleOpen('unblock')} 
            size="small" 
            color="secondary" 
            variant="outlined"
            className={classes.button}
            startIcon={<LockOpenIcon />}
          >
            {translate('osfc.storage_ui.block.unblock_button', { count: blockedEntities.length })}
          </Button>
        )}
      </div>

      <ActionDialog open={open} loading={loading} title={getDialogTitle()}>
        <FormWithRedirect
            record={record}
            submitOnEnter={false}
            component={DialogContent}
            save={handleSubmit}
            initialValues={initialValues}
            render={({ handleSubmitWithRedirect }) => (
              <>
                {!isUnblockMode ? (
                  <>
                    <TerminalDatesInput 
                      component={DateTimeInput} 
                      priorityRecord={record}
                      margin="dense" 
                      source="lock_datetime_from" 
                      label="resources.ports.fields.lock_datetime_from"
                      record={record}
                      fullWidth
                      disabled={loading}
                    />
                    
                    <TerminalDatesInput 
                      component={DateTimeInput} 
                      priorityRecord={record}
                      margin="dense" 
                      source="lock_datetime_to" 
                      label="resources.ports.fields.lock_datetime_to"
                      record={record}
                      fullWidth
                      disabled={loading}
                    />
                    
                    <TextInput 
                      source="comment" 
                      multiline 
                      rows={2} 
                      label="resources.ports.fields.comment"
                      fullWidth
                      record={record}
                      margin="dense"
                      disabled={loading}
                    />
                  </>
                ) : (
                  <Typography variant="body2" className={classes.confirmText}>
                    {translate('osfc.storage_ui.block.confirm_unblock', { entities: entitiesToProcess.length > 1 ? entityPlural : entitySingular })}
                  </Typography>
                )}

                {entitiesToProcess.length > 1 && (
                  <SelectedEntitiesList
                    title={translate(isUnblockMode ? 'osfc.storage_ui.block.blocked_entities' : 'osfc.storage_ui.affected_entities', { entities: entityPlural, count: entitiesToProcess.length })}
                    entities={entitiesToProcess.map(entity => ({ id: entity[idField], name: entity[displayField] }))}
                  />
                )}

                <DialogConfirmActions
                  loading={loading}
                  confirmLabel={translate(isUnblockMode ? 'osfc.storage_ui.block.unblock_action' : 'osfc.storage_ui.block.block_action')}
                  onCancel={handleClose}
                  onConfirm={() => handleSubmitWithRedirect()}
                />
              </>
            )}
          />
      </ActionDialog>
    </>
  );
};